import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { ContentCard } from "@/components/ui/ContentCard";
import { Textarea } from "@/components/ui/textarea";
import { Copy, Download, Printer, Sparkles, Loader2, Pencil, Check, X, FileText } from "lucide-react";
import { downloadHtmlAsWord } from "@/lib/exportWordDoc";
import { getPasteShortcut } from "@/lib/exportGoogleDoc";
import { GoogleDocExportButton } from "@/components/restoration/GoogleDocExportButton";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { supabase } from "@/lib/supabase";
import html2pdf from "html2pdf.js";

type GradeLevel = "K-2" | "3-5" | "6-8" | "9-12";

interface ISSRestorativeTasksProps {
  gradeLevel: GradeLevel;
  incidentDescription: string;
  studentReflection: string;
  studentIdentifier: string;
}

interface RestorativeTask {
  title: string;
  duration: string;
  studentInstructions: string;
  writingSpace: string;
}

const STUDENT_TASKS: Record<GradeLevel, RestorativeTask[]> = {
  "K-2": [
    {
      title: "My Feelings Drawing",
      duration: "10–15 min",
      studentInstructions: "Draw three pictures in the boxes below. In the first box, draw how you felt BEFORE what happened. In the second box, draw how you felt DURING what happened. In the third box, draw how you feel RIGHT NOW. You can use any colors you want. There are no wrong answers.",
      writingSpace: "Box 1 — Before:\n\n\n\nBox 2 — During:\n\n\n\nBox 3 — Right now:\n\n\n",
    },
    {
      title: "My Sorry Note",
      duration: "10 min",
      studentInstructions: "Think about the person who had a hard time because of what happened. Write them a short note — you can draw a picture too if you want. Fill in the blanks below.",
      writingSpace: "Dear _______________,\n\nI want you to know _________________________________\n\n_________________________________________________\n\nNext time, I will __________________________________\n\n_________________________________________________\n\nFrom, _______________",
    },
    {
      title: "My Plan for Next Time",
      duration: "10 min",
      studentInstructions: "Sometimes things happen and we wish we did something different. Think about what happened. Now let's make a plan for next time! Fill in each circle below.",
      writingSpace: "🔴 STOP — When I feel upset, I will: _________________\n\n_________________________________________________\n\n🟡 THINK — I will ask myself: ______________________\n\n_________________________________________________\n\n🟢 GO — Instead, I will: ___________________________\n\n_________________________________________________",
    },
    {
      title: "Kindness Detective",
      duration: "15 min",
      studentInstructions: "You are a Kindness Detective! Your job is to look around and find 5 kind things that people do. Write or draw what you see. Then pick ONE kind thing you want to try today.",
      writingSpace: "Kind thing 1: ____________________________________\n\nKind thing 2: ____________________________________\n\nKind thing 3: ____________________________________\n\nKind thing 4: ____________________________________\n\nKind thing 5: ____________________________________\n\nThe kind thing I want to try: ________________________\n\n_________________________________________________",
    },
  ],
  "3-5": [
    {
      title: "Three Sides of the Story",
      duration: "15–20 min",
      studentInstructions: "Every situation looks different depending on who you ask. Pretend you're a news reporter. Write about what happened from THREE different points of view. Try to be fair — even if it's hard.",
      writingSpace: "MY point of view:\n_________________________________________________\n_________________________________________________\n_________________________________________________\n\nTHE OTHER PERSON'S point of view:\n_________________________________________________\n_________________________________________________\n_________________________________________________\n\nA BYSTANDER'S point of view (someone who saw it happen):\n_________________________________________________\n_________________________________________________\n_________________________________________________",
    },
    {
      title: "My Trigger Map",
      duration: "15 min",
      studentInstructions: "Something set you off. Let's figure out what happened inside you — and then draw a new route for next time. Fill in the road map below.",
      writingSpace: "THE ROAD I TOOK:\nTrigger (what set me off): __________________________\n→ Body signal (what my body felt): ___________________\n→ What I did: ____________________________________\n→ What happened because of it: _____________________\n\nTHE DETOUR (a better route):\nTrigger (same thing): ______________________________\n→ Body signal (same feeling): _______________________\n→ What I could do INSTEAD: ________________________\n→ What would probably happen: _____________________",
    },
    {
      title: "My Accountability Letter",
      duration: "20 min",
      studentInstructions: "This letter is for YOU — not for punishment. It's a chance to be honest about what happened. Nobody is perfect, and owning your part is actually really brave. Answer each section honestly.",
      writingSpace: "1. What happened (my honest version):\n_________________________________________________\n_________________________________________________\n\n2. Who was affected and how:\n_________________________________________________\n_________________________________________________\n\n3. The part I played:\n_________________________________________________\n_________________________________________________\n\n4. What I want to do about it:\n_________________________________________________\n_________________________________________________",
    },
    {
      title: "The Ripple Effect",
      duration: "15 min",
      studentInstructions: "When you drop a rock in water, the ripples spread out far. Actions work the same way — they affect more people than you might think. Draw yourself in the middle, then draw lines to everyone affected. Next to each person, write how they might have been affected.",
      writingSpace: "People affected and how:\n\nPerson 1: _________________ How: __________________\n\nPerson 2: _________________ How: __________________\n\nPerson 3: _________________ How: __________________\n\nPerson 4: _________________ How: __________________\n\nPerson I didn't think of at first: ______________________\nHow: _____________________________________________",
    },
  ],
  "6-8": [
    {
      title: "Root Cause Analysis",
      duration: "20 min",
      studentInstructions: "Most behavior makes sense when you understand the need behind it. This isn't about excuses — it's about understanding yourself better so the same thing doesn't keep happening. Be honest with yourself here.",
      writingSpace: "1. What I did (the behavior):\n_________________________________________________\n_________________________________________________\n\n2. What I was actually feeling underneath:\n_________________________________________________\n\n3. What I actually needed in that moment:\n_________________________________________________\n\n4. Three other ways I could have gotten that need met:\n   a) ____________________________________________\n   b) ____________________________________________\n   c) ____________________________________________\n\n5. What gets in the way of choosing those instead?\n_________________________________________________\n_________________________________________________",
    },
    {
      title: "Conversation Prep",
      duration: "15–20 min",
      studentInstructions: "If you're going to have a face-to-face conversation with the person you affected, this helps you prepare. It's easier to say hard things when you've thought about them first.",
      writingSpace: "1. What I want them to know:\n_________________________________________________\n_________________________________________________\n\n2. What I think they felt (be honest, even if it's uncomfortable):\n_________________________________________________\n_________________________________________________\n\n3. What I'm willing to do to make it right:\n_________________________________________________\n_________________________________________________\n\n4. What I need from them to actually move forward:\n_________________________________________________\n_________________________________________________",
    },
    {
      title: "Pattern Breaker",
      duration: "20 min",
      studentInstructions: "Think about whether something like this has happened before. If it has, that's not a judgment — it's a clue. Finding the pattern is the first step to breaking it.",
      writingSpace: "TIME 1: What happened ____________________________\n  What was going on for me: _________________________\n  What I did: _____________________________________\n\nTIME 2: What happened ____________________________\n  What was going on for me: _________________________\n  What I did: _____________________________________\n\nTHIS TIME: What happened _________________________\n  What was going on for me: _________________________\n  What I did: _____________________________________\n\nThe pattern I see: _________________________________\nThe earliest place I could break it: ____________________\nWhat I need to break it: ____________________________",
    },
    {
      title: "Peer Perspective Reflection",
      duration: "15 min",
      studentInstructions: "Imagine your best friend is asking you these questions — not an adult, not a teacher. Just someone your age who actually wants to understand. Answer honestly, like you'd talk to them.",
      writingSpace: "Friend: \"So what happened?\"\n_________________________________________________\n_________________________________________________\n\nFriend: \"How do you think the other person felt?\"\n_________________________________________________\n_________________________________________________\n\nFriend: \"If I did the same thing, what would you tell me to do differently?\"\n_________________________________________________\n_________________________________________________\n\nFriend: \"Real talk — are you good?\"\n_________________________________________________\n_________________________________________________",
    },
  ],
  "9-12": [
    {
      title: "Decision Autopsy",
      duration: "20–25 min",
      studentInstructions: "Every situation has a chain of small decisions that led to the moment things went wrong. Map out that chain. Find the exit ramp you missed. This isn't about blame — it's about understanding your own decision-making so you can change it.",
      writingSpace: "Decision chain (each moment you made a choice):\n1. _______________________________________________\n2. _______________________________________________\n3. _______________________________________________\n4. _______________________________________________\n5. _______________________________________________\n\nThe exit ramp I missed (where I could have chosen differently):\n_________________________________________________\n\nWhat I would have needed to take that exit:\n_________________________________________________\n\nWhat gets in the way of seeing exit ramps in the moment:\n_________________________________________________",
    },
    {
      title: "Accountability Statement",
      duration: "20 min",
      studentInstructions: "Write a statement you'd be willing to read aloud — to an adult, to the person affected, or to yourself. This isn't a forced apology. It's YOUR words about what happened. Mark which parts you're willing to share and which are just for you.",
      writingSpace: "What I did:\n_________________________________________________\n_________________________________________________\n[ ] Willing to share  [ ] Private\n\nWhy I think it happened:\n_________________________________________________\n_________________________________________________\n[ ] Willing to share  [ ] Private\n\nThe impact, even if unintended:\n_________________________________________________\n_________________________________________________\n[ ] Willing to share  [ ] Private\n\nWhat I'm choosing to do now:\n_________________________________________________\n_________________________________________________\n[ ] Willing to share  [ ] Private",
    },
    {
      title: "Systems + Self Reflection",
      duration: "20–25 min",
      studentInstructions: "Behavior doesn't happen in a vacuum. Sometimes the environment plays a role. This reflection asks you to be honest about both — what you contributed AND what the situation contributed. Neither cancels the other out.",
      writingSpace: "1. Was there anything about the situation, class, or environment that contributed to what happened?\n_________________________________________________\n_________________________________________________\n\n2. What would you change about the situation if you could?\n_________________________________________________\n\n3. What can you actually control in this situation?\n_________________________________________________\n\n4. What role did you play, regardless of the environment?\n_________________________________________________\n\n5. If you could advocate for a change that would help, what would it be?\n_________________________________________________",
    },
    {
      title: "Letter to Future Me",
      duration: "15–20 min",
      studentInstructions: "Write a letter to yourself 6 months from now. Be completely honest — no one else will read this unless you choose to share it. Tell future-you what happened, what you learned, and what you hope has changed.",
      writingSpace: "Dear Future Me,\n\nToday is ______________________ and something happened that I need to tell you about.\n\nWhat happened:\n_________________________________________________\n_________________________________________________\n_________________________________________________\n\nWhat I learned about myself:\n_________________________________________________\n_________________________________________________\n\nWhat I hope has changed by the time you read this:\n_________________________________________________\n_________________________________________________\n\n— Me",
    },
  ],
};

export const ISSRestorativeTasks = ({
  gradeLevel,
  incidentDescription,
  studentReflection,
  studentIdentifier,
}: ISSRestorativeTasksProps) => {
  const [selectedTasks, setSelectedTasks] = useState<number[]>([]);
  const [customTask, setCustomTask] = useState<string | null>(null);
  const [editedCustomTask, setEditedCustomTask] = useState<string | null>(null);
  const [isEditingCustom, setIsEditingCustom] = useState(false);
  const [includeCustom, setIncludeCustom] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const tasks = STUDENT_TASKS[gradeLevel];
  const pasteShortcut = getPasteShortcut();

  const toggleTask = (idx: number) => {
    setSelectedTasks((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
    );
  };

  const generateCustomTask = async () => {
    setIsGenerating(true);
    try {
      const { data } = await supabase.functions.invoke("coach-chat", {
        body: {
          message: `Generate a SINGLE custom restorative reflection task written FOR A STUDENT to complete. This is for a ${gradeLevel} grade student. Write the instructions in age-appropriate language, directly addressing the student (use "you").

The task must be specific to THIS incident — not generic.

Incident: ${incidentDescription}
Student reflection: ${studentReflection || "Not yet received"}

Format it exactly like this:
Title (duration)

[Student instructions - 2-3 sentences telling the student what to do, written in second person]

[Writing/response space with fill-in-the-blank prompts or open-ended questions - include at least 3 specific prompts with blank lines]

Keep the tone respectful but direct. Age-appropriate for ${gradeLevel}. This is about understanding, not punishment.`,
          assistant_config_id: "4e247984-96e4-44a3-bb66-42077b044d33",
        },
      });

      const content = data?.content || getLocalFallbackTask(gradeLevel, incidentDescription);
      setCustomTask(content);
      setEditedCustomTask(content);
      setIncludeCustom(true);
      setIsEditingCustom(false);
    } catch {
      const content = getLocalFallbackTask(gradeLevel, incidentDescription);
      setCustomTask(content);
      setEditedCustomTask(content);
      setIncludeCustom(true);
    } finally {
      setIsGenerating(false);
    }
  };

  const activeCustomTask = editedCustomTask ?? customTask;

  const buildPrintHTML = useMemo(() => {
    const selected = selectedTasks.map((i) => tasks[i]);
    const isYoung = gradeLevel === "K-2" || gradeLevel === "3-5";
    const fontSize = isYoung ? "12pt" : "11pt";

    let html = `<div style="font-family:'Segoe UI',system-ui,sans-serif;padding:28px 36px;max-width:680px;margin:0 auto;color:#1a1a1a;">`;
    html += `<div style="border-bottom:2px solid #0D4436;padding-bottom:8px;margin-bottom:16px;">`;
    html += `<div style="font-size:16pt;font-weight:700;color:#0D4436;">My Reflection Tasks</div>`;
    html += `<div style="font-size:9pt;color:#6b7280;margin-top:4px;">Name: ${studentIdentifier || "_______________"} · Grade ${gradeLevel} · Date: ${new Date().toLocaleDateString()}</div>`;
    html += `</div>`;

    selected.forEach((task) => {
      html += `<div style="margin-bottom:24px;page-break-inside:avoid;">`;
      html += `<div style="font-size:13pt;font-weight:700;color:#0D4436;margin-bottom:4px;">${task.title} <span style="font-weight:400;font-size:10pt;color:#6b7280;">(${task.duration})</span></div>`;
      html += `<div style="font-size:${fontSize};line-height:1.6;color:#374151;margin-bottom:8px;background:#fef3c7;padding:8px 12px;border-radius:6px;border-left:3px solid #ca8a04;">${task.studentInstructions}</div>`;
      html += `<div style="font-size:${fontSize};line-height:2.2;color:#1a1a1a;white-space:pre-wrap;">${task.writingSpace.replace(/_{3,}/g, '<span style="border-bottom:1px solid #9ca3af;display:inline-block;min-width:200px;">&nbsp;</span>')}</div>`;
      html += `</div>`;
    });

    if (activeCustomTask && includeCustom) {
      html += `<div style="margin-bottom:24px;page-break-inside:avoid;border:1px solid #ca8a04;border-radius:8px;padding:12px;">`;
      html += `<div style="font-size:${fontSize};line-height:2;color:#1a1a1a;white-space:pre-wrap;">${activeCustomTask}</div>`;
      html += `</div>`;
    }

    html += `<div style="margin-top:16px;text-align:center;font-size:8pt;color:#9ca3af;border-top:1px solid #e5e7eb;padding-top:8px;">Provided by your school · Generated by HeyBodhi</div>`;
    html += `</div>`;
    return html;
  }, [selectedTasks, tasks, activeCustomTask, includeCustom, gradeLevel, studentIdentifier]);

  const buildGoogleDocsHTML = useMemo(() => {
    const selected = selectedTasks.map((i) => tasks[i]);
    let html = `<h1>My Reflection Tasks</h1>`;
    html += `<p>Name: ${studentIdentifier || "_______________"} · Grade ${gradeLevel} · Date: ${new Date().toLocaleDateString()}</p><hr>`;

    selected.forEach((task) => {
      html += `<h2>${task.title} (${task.duration})</h2>`;
      html += `<blockquote><p>${task.studentInstructions}</p></blockquote>`;
      const lines = task.writingSpace.split("\n");
      lines.forEach(line => {
        if (line.trim() === "") {
          html += `<br>`;
        } else {
          html += `<p>${line}</p>`;
        }
      });
      html += `<hr>`;
    });

    if (activeCustomTask && includeCustom) {
      html += `<h2>Custom Task</h2>`;
      activeCustomTask.split("\n").forEach(line => {
        if (line.trim() === "") html += `<br>`;
        else html += `<p>${line}</p>`;
      });
    }

    html += `<p><small>Provided by your school · Generated by HeyBodhi</small></p>`;
    return html;
  }, [selectedTasks, tasks, activeCustomTask, includeCustom, gradeLevel, studentIdentifier]);

  const buildStudentPacketText = useMemo(() => {
    const selected = selectedTasks.map((i) => tasks[i]);
    const lines = [
      `MY REFLECTION TASKS`,
      `Name: ${studentIdentifier || "_______________"} · Grade ${gradeLevel} · Date: ${new Date().toLocaleDateString()}`,
      ``,
    ];

    selected.forEach((task, i) => {
      lines.push(
        `━━━ ${task.title} (${task.duration}) ━━━`,
        ``,
        task.studentInstructions,
        ``,
        task.writingSpace,
        ``,
      );
    });

    if (activeCustomTask && includeCustom) {
      lines.push(`━━━ Custom Task ━━━`, ``, activeCustomTask, ``);
    }

    lines.push(`Provided by your school · Generated by HeyBodhi`);
    return lines.join("\n");
  }, [selectedTasks, tasks, activeCustomTask, includeCustom, gradeLevel, studentIdentifier]);

  const handleCopyRichText = async () => {
    try {
      if (typeof ClipboardItem !== "undefined") {
        await navigator.clipboard.write([
          new ClipboardItem({
            "text/html": new Blob([buildGoogleDocsHTML], { type: "text/html" }),
            "text/plain": new Blob([buildStudentPacketText], { type: "text/plain" }),
          }),
        ]);
      } else {
        await navigator.clipboard.writeText(buildStudentPacketText);
      }
      toast({ title: "Copied with formatting! Paste into Google Docs or Word" });
    } catch {
      try {
        await navigator.clipboard.writeText(buildStudentPacketText);
        toast({ title: "Copied as text" });
      } catch {
        toast({
          title: "Could not copy to clipboard",
          description: "Try using the Download PDF or Print option instead.",
          variant: "destructive",
        });
      }
    }
  };

  const handlePrint = () => {
    const printWindow = window.open("", "_blank", "noopener,noreferrer,width=800,height=900");
    if (!printWindow) {
      toast({
        title: "Could not open print window",
        description: "Check your browser's popup blocker settings and try again.",
        variant: "destructive",
      });
      return;
    }
    printWindow.document.open();
    printWindow.document.write(`<!DOCTYPE html><html><head><title>My Reflection Tasks</title><meta charset="utf-8"/></head><body>${buildPrintHTML}</body></html>`);
    printWindow.document.close();
    printWindow.focus();
    // Delay allows the window to finish rendering before the print dialog opens.
    window.setTimeout(() => {
      try { printWindow.print(); } catch (e) { console.error(e); }
    }, 300);
  };

  const handleDownloadPDF = async () => {
    try {
      const element = document.createElement("div");
      element.innerHTML = buildPrintHTML;
      await (html2pdf() as {
        set: (opts: unknown) => { from: (el: HTMLElement) => { save: () => Promise<void> } };
      })
        .set({
          margin: [0.4, 0.5, 0.4, 0.5],
          filename: `reflection-tasks-${studentIdentifier || "student"}-${new Date().toISOString().split("T")[0]}.pdf`,
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
        })
        .from(element)
        .save();
    } catch (err) {
      console.error("[ISSRestorativeTasks] PDF generation failed", err);
      toast({
        title: "Could not generate PDF",
        description: "Check your browser's download settings and try again.",
        variant: "destructive",
      });
    }
  };

  const handleDownloadWord = () => {
    try {
      downloadHtmlAsWord(
        buildGoogleDocsHTML,
        `reflection-tasks-${studentIdentifier || "student"}-${new Date().toISOString().split("T")[0]}.doc`,
        `Reflection Tasks - ${studentIdentifier || "Student"}`,
      );
      toast({ title: "Word document downloaded", description: "Open it in Word, Pages, or LibreOffice." });
    } catch (err) {
      console.error("[ISSRestorativeTasks] Word generation failed", err);
      toast({
        title: "Could not create Word document",
        description: "Try Download PDF or Print instead.",
        variant: "destructive",
      });
    }
  };

  return (
    <ContentCard>
      <h2 className="text-lg font-semibold text-bodhi-teal font-literata mb-1">
        Restorative Tasks for ISS
      </h2>
      <p className="text-sm text-gray-600 mb-4">
        Select tasks for the student. These are written for the student to complete — you can print, download a PDF, or copy into Google Docs.
      </p>

      <div className="space-y-2 mb-4">
        {tasks.map((task, idx) => {
          const isSelected = selectedTasks.includes(idx);
          return (
            <button
              key={idx}
              onClick={() => toggleTask(idx)}
              className={cn(
                "w-full text-left rounded-xl border-2 p-3 transition-all",
                isSelected
                  ? "border-bodhi-teal bg-bodhi-teal/5"
                  : "border-gray-200 bg-white hover:border-gray-300"
              )}
            >
              <div className="flex items-start gap-2">
                <span className={cn(
                  "w-5 h-5 mt-0.5 rounded-full border-2 flex items-center justify-center flex-shrink-0 text-[10px]",
                  isSelected ? "bg-bodhi-teal border-bodhi-teal text-white" : "border-gray-300"
                )}>
                  {isSelected && "✓"}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm text-gray-800">{task.title}</span>
                    <span className="text-xs text-gray-400 flex-shrink-0">{task.duration}</span>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">{task.studentInstructions}</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Custom Task */}
      <div className="mb-4 pt-3 border-t border-gray-100">
        <div className="flex flex-wrap gap-2 mb-3">
          <Button
            variant="outline"
            size="sm"
            onClick={generateCustomTask}
            disabled={isGenerating}
            className="text-bodhi-gold border-bodhi-gold/30 hover:bg-bodhi-gold/5"
          >
            {isGenerating ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
            Generate Custom Task for This Incident
          </Button>
        </div>

        {customTask && (
          <ContentCard className="bg-bodhi-gold/5 border border-bodhi-gold/20 p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Sparkles className="w-3 h-3 text-bodhi-gold" />
                <span className="text-xs font-semibold text-gray-700">AI-Generated Custom Task</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIncludeCustom(!includeCustom)}
                  className={cn(
                    "text-xs px-2 py-1 rounded-full border transition-colors",
                    includeCustom
                      ? "bg-bodhi-teal text-white border-bodhi-teal"
                      : "bg-white text-gray-500 border-gray-300"
                  )}
                >
                  {includeCustom ? "✓ Included" : "Excluded"}
                </button>
                {!isEditingCustom ? (
                  <Button variant="ghost" size="sm" onClick={() => setIsEditingCustom(true)} className="h-6 text-xs text-gray-500">
                    <Pencil className="w-3 h-3 mr-1" /> Edit
                  </Button>
                ) : (
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => setIsEditingCustom(false)} className="h-6 text-xs text-bodhi-teal">
                      <Check className="w-3 h-3 mr-1" /> Done
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => { setEditedCustomTask(customTask); setIsEditingCustom(false); }} className="h-6 text-xs text-gray-400">
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                )}
                <Button variant="ghost" size="sm" onClick={() => { setCustomTask(null); setEditedCustomTask(null); setIsEditingCustom(false); }} className="h-6 text-xs text-gray-400">
                  Remove
                </Button>
              </div>
            </div>

            {isEditingCustom ? (
              <Textarea
                value={editedCustomTask ?? customTask}
                onChange={(e) => setEditedCustomTask(e.target.value)}
                className="min-h-[150px] text-sm font-sans leading-relaxed"
                rows={6}
              />
            ) : (
              <div className={cn("text-sm text-gray-700 whitespace-pre-wrap", !includeCustom && "opacity-50")}>
                {activeCustomTask}
              </div>
            )}
          </ContentCard>
        )}
      </div>

      {/* Share/Export */}
      {(selectedTasks.length > 0 || (customTask && includeCustom)) && (
        <div className="pt-3 border-t border-gray-100">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Share with student</p>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={handleCopyRichText} className="border-bodhi-teal/30 text-bodhi-teal hover:bg-bodhi-teal/5">
              <Copy className="w-3 h-3 mr-1" /> Copy to Clipboard
            </Button>
            <GoogleDocExportButton
              html={buildGoogleDocsHTML}
              plain={buildStudentPacketText}
              docTitle={`Reflection Tasks - ${studentIdentifier || "Student"} - ${new Date().toLocaleDateString()}`}
              label="Copy & open Google Doc"
              dialogTitle="Copy tasks into a Google Doc"
              className="border-gray-300 text-gray-700 hover:bg-gray-100"
            />
            <Button variant="outline" size="sm" onClick={handleDownloadWord} className="border-gray-300 text-gray-700 hover:bg-gray-100">
              <FileText className="w-3 h-3 mr-1" /> Download Word
            </Button>
            <Button variant="outline" size="sm" onClick={handleDownloadPDF} className="border-gray-300 text-gray-700 hover:bg-gray-100">
              <Download className="w-3 h-3 mr-1" /> Download PDF
            </Button>
            <Button variant="outline" size="sm" onClick={handlePrint} className="border-gray-300 text-gray-700 hover:bg-gray-100">
              <Printer className="w-3 h-3 mr-1" /> Print
            </Button>
          </div>
          <p className="text-xs text-gray-500 mt-2 leading-relaxed">
            <span className="font-medium text-gray-600">Google Docs:</span> we copy the tasks for you, open a blank doc, then you paste with{" "}
            <kbd className="px-1 py-0.5 rounded border border-gray-200 bg-gray-50 font-mono text-[11px]">{pasteShortcut}</kbd>.
            For a ready-to-print file, use Download PDF or Print instead.
          </p>
        </div>
      )}

    </ContentCard>
  );
};

function getLocalFallbackTask(gradeLevel: GradeLevel, incident: string): string {
  const isYoung = gradeLevel === "K-2" || gradeLevel === "3-5";
  if (isYoung) {
    return `My Thinking Time (15 minutes)\n\nSomething happened today, and the adults at school want to help you think about it — not to get you in trouble, but to help you learn from it.\n\n1. What happened today?\n_______________________________________________\n_______________________________________________\n\n2. How did you feel when it happened?\n_______________________________________________\n\n3. Who else was affected?\n_______________________________________________\n\n4. What is one thing you could do differently next time?\n_______________________________________________\n_______________________________________________\n\n5. What do you need from the adults here to help you?\n_______________________________________________`;
  }
  return `Incident-Specific Reflection (20 minutes)\n\nThis is your space to think honestly about what happened. No one is here to lecture you — the goal is understanding, not punishment.\n\n1. What happened, from your perspective?\n_______________________________________________\n_______________________________________________\n_______________________________________________\n\n2. What were you trying to get or protect in that moment?\n_______________________________________________\n_______________________________________________\n\n3. Who was affected, and what do you think it was like for them?\n_______________________________________________\n_______________________________________________\n\n4. What's one thing you could practice doing differently next time this feeling comes up?\n_______________________________________________\n_______________________________________________\n\n5. What do you need from the adults around you right now?\n_______________________________________________`;
}
